import { OrganizationPractitioner } from "@/hooks/useOrganizationPractitioners";

import { fhirR4 } from "@smile-cdr/fhirts";

import { LOINC_SYSTEM, PRACTITIONER_ROLE_CODING, SNOMED_SYSTEM, normalizeRoleDisplay } from "./fhir-constants";
import { FhirResourceType, formatHumanName, formatters } from "./fhir-utils";

/**
 * System URL for practitioner role codes
 */
export const PRACTITIONER_ROLE_SYSTEM = "http://terminology.hl7.org/CodeSystem/practitioner-role";
export const RMO_ROLE_CODE = "303133007"; // Responsible medical officer
export const RMO_ROLE_DISPLAY = "MRP";
export { SNOMED_SYSTEM };

/**
 * SNOMED CT code for default Medical practitioner role
 */
export const MEDICAL_PRACTITIONER_ROLE_CODE = "158965000";
export const MEDICAL_PRACTITIONER_ROLE_DISPLAY = "Medical practitioner";

export const CONDITION_BASED_CARE_TEAM_CATEGORY_CODE = "LA27978-8";

/**
 * Practitioner role codes
 */
export enum PractitionerRole {
  NURSE = "nurse",
  NURSE_PRACTITIONER = "nurse-practitioner",
  PHYSICIAN = "physician",
}

const hasConditionFocusedCategory = (ct: fhirR4.CareTeam): boolean =>
  ct.category?.some(({ coding }) =>
    coding?.some(c => c.system === LOINC_SYSTEM && c.code === CONDITION_BASED_CARE_TEAM_CATEGORY_CODE)
  ) ?? false;

/** pick the "clinical" CareTeam: the one with the condition-focused category (LOINC LA27978-8) */
export function pickClinicalCareTeam(careTeams: fhirR4.CareTeam[]): fhirR4.CareTeam | undefined {
  return careTeams.find(hasConditionFocusedCategory);
}

/**
 * Build a new draft clinical CareTeam for a patient (no participants yet).
 * The condition-focused category (LOINC LA27978-8) marks it as the clinical
 * CareTeam that `pickClinicalCareTeam` selects. Used when a patient has no
 * clinical CareTeam yet and one must be created (e.g. first MRP assignment).
 */
export function buildDraftClinicalCareTeam(patientId: string, parentOrgRef?: string): fhirR4.CareTeam {
  return {
    resourceType: FhirResourceType.CareTeam,
    status: "active",
    subject: { reference: formatters.formatPatient(patientId) },
    category: [
      {
        coding: [
          {
            system: LOINC_SYSTEM,
            code: CONDITION_BASED_CARE_TEAM_CATEGORY_CODE,
            display: "Condition-focused care team",
          },
        ],
      },
    ],
    ...(parentOrgRef ? { managingOrganization: [{ reference: parentOrgRef }] } : {}),
    participant: [],
  };
}

/**
 * Get clinic Organization reference from CareTeam participant
 * The clinic is stored as a participant with member.reference = "Organization/clinic-id"
 */
export function getClinicFromCareTeam(careTeam?: fhirR4.CareTeam): string | undefined {
  if (!careTeam?.participant) {
    return undefined;
  }

  // Find the participant that references an Organization (the clinic)
  const clinicParticipant = careTeam.participant.find(p =>
    p.member?.reference?.startsWith(`${FhirResourceType.Organization}/`)
  );

  return clinicParticipant?.member?.reference;
}

/**
 * THE canonical way to get a patient's clinic reference: picks the clinical
 * CareTeam and extracts its Organization participant reference.
 * Use this for any clinic comparison/filtering; use resolveClinicName for display.
 * Note: patient.managingOrganization is the parent organization, NOT the clinic —
 * never use it as a clinic source.
 */
export function getPatientClinicRef(careTeams: fhirR4.CareTeam[]): string | undefined {
  return getClinicFromCareTeam(pickClinicalCareTeam(careTeams));
}

/**
 * THE canonical way to display a patient's clinic name.
 * Resolves the clinic reference via getPatientClinicRef and looks up its label
 * from the clinics array, falling back to the raw reference, then to the
 * provided fallback string when the patient has no CareTeam clinic.
 */
export function resolveClinicName(
  careTeams: fhirR4.CareTeam[],
  clinics: { value: string; label: string }[],
  fallback?: string
): string {
  const clinicRef = getPatientClinicRef(careTeams);
  if (!clinicRef) {
    return fallback ?? "";
  }
  return clinics.find(c => c.value === clinicRef)?.label || clinicRef;
}

export function getMrpFromCareTeam(careTeam?: fhirR4.CareTeam): string {
  const parts = careTeam?.participant ?? [];
  const mrp = parts.find(p =>
    (p.role ?? []).some(role => (role.coding ?? []).some(c => c.system === SNOMED_SYSTEM && c.code === RMO_ROLE_CODE))
  );
  return mrp?.member?.reference ?? "";
}

export function makeRmoRole(): fhirR4.CodeableConcept {
  return {
    coding: [{ system: SNOMED_SYSTEM, code: RMO_ROLE_CODE, display: RMO_ROLE_DISPLAY }],
    text: RMO_ROLE_DISPLAY,
  };
}

export function makeDefaultPractitionerRole(): fhirR4.CodeableConcept {
  return {
    coding: [
      { system: SNOMED_SYSTEM, code: MEDICAL_PRACTITIONER_ROLE_CODE, display: MEDICAL_PRACTITIONER_ROLE_DISPLAY },
    ],
    text: MEDICAL_PRACTITIONER_ROLE_DISPLAY,
  };
}

const roleKey = (coding: fhirR4.Coding): string => `${coding.system ?? ""}|${coding.code ?? ""}`;

/**
 * Canonical English role codings this app writes, keyed by `system|code`: the three
 * PRACTITIONER_ROLE_CODING options, the default "Medical practitioner", and the MRP role.
 * Used to re-derive a role's display after it was read under a translating Accept-Language.
 */
const CANONICAL_ROLE_BY_KEY: Record<string, fhirR4.Coding> = Object.fromEntries(
  [
    ...Object.values(PRACTITIONER_ROLE_CODING),
    { system: SNOMED_SYSTEM, code: MEDICAL_PRACTITIONER_ROLE_CODE, display: MEDICAL_PRACTITIONER_ROLE_DISPLAY },
    { system: SNOMED_SYSTEM, code: RMO_ROLE_CODE, display: RMO_ROLE_DISPLAY },
  ].map(coding => [roleKey(coding), coding])
);

/**
 * Re-derives a role concept's displays from constants so a role fetched under a non-English
 * `Accept-Language` is not written back translated. Each coding whose `system`+`code` is a known
 * role is replaced with its canonical English coding, and `text` is set from the first such match.
 * A coding we do not recognise is left untouched: an unknown role is not in the translation store,
 * so its display is already English and MUST survive.
 *
 * @param role - a PractitionerRole role CodeableConcept, possibly carrying a translated display.
 * @returns the same role with known codings and text in English.
 */
export function canonicalizeRoleConcept(role: fhirR4.CodeableConcept): fhirR4.CodeableConcept {
  if (!role.coding?.length) {
    return role;
  }
  let canonicalText: string | undefined;
  const coding = role.coding.map(c => {
    const canonical = CANONICAL_ROLE_BY_KEY[roleKey(c)];
    if (canonical) {
      canonicalText ??= canonical.display;
      return { ...canonical };
    }
    return c;
  });
  return { ...role, coding, ...(canonicalText !== undefined ? { text: canonicalText } : {}) };
}

/**
 * Apply {@link canonicalizeRoleConcept} to every role on every participant. MUST run on the final
 * participant array before a CareTeam PUT or a `replace /participant` PATCH, so members the form did
 * not touch (survivors carried from the fetched resource) are written back with English role displays.
 *
 * @param participants - the CareTeam participant array about to be persisted.
 * @returns a new array with each role canonicalized; non-role participants (e.g. the clinic) are unchanged.
 */
export function canonicalizeParticipantRoles(participants: fhirR4.CareTeamParticipant[]): fhirR4.CareTeamParticipant[] {
  return participants.map(p => (p.role?.length ? { ...p, role: p.role.map(canonicalizeRoleConcept) } : p));
}

/**
 * Set/replace clinic in CareTeam participant
 * The clinic is stored as a participant with member.reference = "Organization/clinic-id"
 * managingOrganization remains unchanged (holds parent organization)
 */
export function setClinicOnCareTeam(careTeam: fhirR4.CareTeam, orgRef: string | ""): fhirR4.CareTeam {
  const updated: fhirR4.CareTeam = { ...careTeam };
  const participants = [...(updated.participant ?? [])];

  // Find existing clinic participant (Organization reference)
  const idx = participants.findIndex(p => p.member?.reference?.startsWith(`${FhirResourceType.Organization}/`));

  if (!orgRef) {
    // Remove clinic participant if clearing
    if (idx >= 0) {
      participants.splice(idx, 1);
    }
    if (participants.length) {
      updated.participant = participants;
    } else {
      delete updated.participant;
    }
    return updated;
  }

  const newParticipant: fhirR4.CareTeamParticipant = {
    member: { reference: orgRef, type: FhirResourceType.Organization },
  };

  if (idx >= 0) {
    // Update existing clinic participant, preserving other fields
    participants[idx] = {
      ...participants[idx],
      member: { reference: orgRef, type: FhirResourceType.Organization },
    };
  } else {
    // Add new clinic participant
    participants.push(newParticipant);
  }

  updated.participant = participants;
  return updated;
}

/**
 * Resolve a CareTeam participant to a bare Practitioner id, following both direct
 * `Practitioner/{id}` members and `PractitionerRole/{id}` members (via the provided roles).
 * Returns "" for non-practitioner participants (e.g. the Organization clinic).
 */
export function getParticipantPractitionerId(
  participant: fhirR4.CareTeamParticipant,
  practitionerRoles: fhirR4.PractitionerRole[] = []
): string {
  const ref = participant?.member?.reference;
  if (isPractitionerRef(ref)) {
    return getPractitionerId(participant);
  }
  if (isPractitionerRoleRef(ref)) {
    const roleId = ref!.split("/").pop();
    const role = practitionerRoles.find(r => r.id === roleId);
    const practitionerRef = role?.practitioner?.reference;
    return practitionerRef?.includes("/") ? (practitionerRef.split("/").pop() ?? "") : (practitionerRef ?? "");
  }
  return "";
}

/**
 * Add a practitioner as a regular (non-MRP) CareTeam member. No-op when the practitioner is
 * already on the team — whether via a direct `Practitioner/{id}` participant OR a
 * `PractitionerRole/{id}` participant (resolved through `practitionerRoles`) — so it never
 * creates a duplicate. Uses the practitioner's PractitionerRole coding when provided,
 * otherwise the default "Medical practitioner" role.
 */
export function addPractitionerToCareTeam(
  careTeam: fhirR4.CareTeam,
  practitionerRef: string,
  role?: fhirR4.CodeableConcept,
  practitionerRoles: fhirR4.PractitionerRole[] = []
): fhirR4.CareTeam {
  const updated: fhirR4.CareTeam = { ...careTeam };
  const participants = [...(updated.participant ?? [])];

  const targetId = practitionerRef.includes("/") ? practitionerRef.split("/").pop() : practitionerRef;
  const onTeamIds = getCareTeamPractitionerIds(careTeam, practitionerRoles);

  if (!targetId || !onTeamIds.has(targetId)) {
    participants.push({
      member: { reference: practitionerRef, type: FhirResourceType.Practitioner },
      // `role` may be a PractitionerRole.code read under a translating Accept-Language, so
      // canonicalize its display rather than persisting the translation.
      role: [role ? canonicalizeRoleConcept(role) : makeDefaultPractitionerRole()],
    });
  }

  updated.participant = participants;
  return updated;
}

/** upsert/remove MRP participant */
export function setMrpOnCareTeam(
  careTeam: fhirR4.CareTeam,
  practitionerRef: string | "",
  practitionerRoles?: fhirR4.PractitionerRole[]
): fhirR4.CareTeam {
  const updated: fhirR4.CareTeam = { ...careTeam };
  const participants = [...(updated.participant ?? [])];

  const isRmo = (p: fhirR4.CareTeamParticipant) =>
    (p.role ?? []).some(role => (role.coding ?? []).some(c => c.system === SNOMED_SYSTEM && c.code === RMO_ROLE_CODE));

  const idx = participants.findIndex(isRmo);

  if (!practitionerRef) {
    // remove RMO participant if clearing
    if (idx >= 0) {
      participants.splice(idx, 1);
    }
    if (participants.length) {
      updated.participant = participants;
    } else {
      delete updated.participant;
    }
    return updated;
  }

  const newParticipant: fhirR4.CareTeamParticipant = {
    member: { reference: practitionerRef, type: FhirResourceType.Practitioner },
    role: [makeRmoRole()],
  };

  if (idx >= 0) {
    const oldMrp = participants[idx];
    const oldRef = oldMrp.member?.reference;

    // Demote old MRP to regular participant if it's a different practitioner
    if (oldRef && oldRef !== practitionerRef) {
      const oldPractitionerId = oldRef.includes("/") ? oldRef.split("/").pop() : oldRef;
      const oldPrRole = practitionerRoles?.find(r => r.practitioner?.reference?.split("/").pop() === oldPractitionerId);
      // The fetched PractitionerRole.code can carry a translated display; write the English one.
      const demotedRole = oldPrRole?.code?.[0]?.coding?.length
        ? canonicalizeRoleConcept(oldPrRole.code[0])
        : makeDefaultPractitionerRole();
      participants[idx] = {
        ...oldMrp,
        role: [demotedRole],
      };
      participants.push(newParticipant);
    } else {
      // Same practitioner — just update the role
      participants[idx] = {
        ...oldMrp,
        member: { reference: practitionerRef, type: FhirResourceType.Practitioner },
        role: [makeRmoRole()],
      };
    }
  } else {
    participants.push(newParticipant);
  }

  updated.participant = participants;
  return updated;
}

/**
 * Extract practitioner name from a Practitioner resource
 * @param practitioner - The FHIR Practitioner resource
 * @returns Formatted practitioner name or empty string
 */
export const getPractitionerName = (practitioner?: fhirR4.Practitioner): string => {
  if (!practitioner?.name || practitioner.name.length === 0) {
    return "";
  }
  return formatHumanName(practitioner.name[0], {
    include: ["given", "family"],
    lineSeparator: " ",
  });
};

/**
 * Extract practitioner role from a CareTeam participant
 * @param participant - The CareTeam participant
 * @returns Role string or empty string
 */
export const getPractitionerRole = (participant?: fhirR4.CareTeamParticipant): string => {
  if (!participant?.role || participant.role.length === 0) {
    return "";
  }

  const roleCodeableConcept = participant.role[0];
  if (!roleCodeableConcept?.coding || roleCodeableConcept.coding.length === 0) {
    return roleCodeableConcept?.text || "";
  }

  // Return the display text from the first coding
  return normalizeRoleDisplay(roleCodeableConcept.coding[0]?.display || roleCodeableConcept.text || "");
};

/**
 * Extract organization/clinic name from a CareTeam participant or Organization resource
 * @param participant - The CareTeam participant
 * @param organization - Optional Organization resource (if resolved)
 * @returns Clinic/facility name or empty string
 */
export const getPractitionerClinic = (
  participant?: fhirR4.CareTeamParticipant,
  organization?: fhirR4.Organization
): string => {
  // First try to get from resolved organization resource
  if (organization?.name) {
    return organization.name;
  }

  // Otherwise check participant's onBehalfOf reference display
  if (participant?.onBehalfOf?.display) {
    return participant.onBehalfOf.display;
  }

  return "";
};

/**
 * Extract phone number from a Practitioner resource
 * @param practitioner - The FHIR Practitioner resource
 * @returns Phone number or empty string
 */
export const getPractitionerPhone = (practitioner?: fhirR4.Practitioner): string => {
  if (!practitioner?.telecom) {
    return "";
  }
  const phone = practitioner.telecom.find(t => t.system === "phone");
  return phone?.value || "";
};

/** Check if a reference string points to a Practitioner resource */
export const isPractitionerRef = (ref?: string): boolean => !!ref?.startsWith(`${FhirResourceType.Practitioner}/`);

/** Check if a reference string points to a PractitionerRole resource */
export const isPractitionerRoleRef = (ref?: string): boolean =>
  !!ref?.startsWith(`${FhirResourceType.PractitionerRole}/`);

/**
 * Check if a CareTeam participant is a Practitioner/ participant
 */
export const isPractitionerParticipant = (participant?: fhirR4.CareTeamParticipant): boolean =>
  isPractitionerRef(participant?.member?.reference);

/**
 * Check if a CareTeam participant is a PractitionerRole/ participant
 */
export const isPractitionerRoleParticipant = (participant?: fhirR4.CareTeamParticipant): boolean =>
  isPractitionerRoleRef(participant?.member?.reference);

/**
 * Extract practitioner ID from a CareTeam participant reference
 * @param participant - The CareTeam participant
 * @returns Practitioner ID or empty string
 */
export const getPractitionerId = (participant?: fhirR4.CareTeamParticipant): string => {
  if (!participant?.member?.reference) {
    return "";
  }
  const match = participant.member.reference.match(new RegExp(`^${FhirResourceType.Practitioner}\\/(.+)$`));
  return match ? match[1] : "";
};

/**
 * Reconcile a CareTeam's practitioner membership against a selected set of Practitioner IDs.
 * Drops any `Practitioner/` participant whose id is not in `selectedPractitionerIds`, while
 * preserving every non-practitioner participant (e.g. the clinic Organization). The MRP is a
 * selected practitioner and is therefore retained.
 *
 * Needed because the backend seeds the CREATING practitioner onto the clinical CareTeam at
 * patient creation; if the user removes themself (or anyone) during the Care Team step, that
 * removal must be persisted rather than silently dropped (VMD-682).
 */
export function removeUnselectedPractitioners(
  careTeam: fhirR4.CareTeam,
  selectedPractitionerIds: Iterable<string>
): fhirR4.CareTeam {
  const selected = selectedPractitionerIds instanceof Set ? selectedPractitionerIds : new Set(selectedPractitionerIds);
  return {
    ...careTeam,
    participant: (careTeam.participant ?? []).filter(
      p => !isPractitionerParticipant(p) || selected.has(getPractitionerId(p))
    ),
  };
}

/**
 * Translation key for practitioner role
 * @param role - The practitioner role code
 * @returns Translation key string
 */
export const translationKeyForPractitionerRole = (role: PractitionerRole): string => {
  return `pages.patientOverview.careTeam.practitioners.roles.${role}`;
};

/**
 * Get all practitioner role codes
 * @returns Array of all PractitionerRole values
 */
export const getAllPractitionerRoles = (): PractitionerRole[] => {
  return Object.values(PractitionerRole);
};

/**
 * Check if a CareTeam participant has the MRP (Most Responsible Physician) role
 * @param participant - The CareTeam participant
 * @returns true if the participant has the MRP role (SNOMED 303133007)
 */
export const isMrpParticipant = (participant?: fhirR4.CareTeamParticipant): boolean => {
  if (!participant?.role) {
    return false;
  }
  return participant.role.some(
    role => role.coding?.some(coding => coding.system === SNOMED_SYSTEM && coding.code === RMO_ROLE_CODE) ?? false
  );
};

/**
 * Resolve the MRP practitioner's display name from a CareTeam and practitioner list.
 * @param careTeam - The CareTeam resource (may be undefined)
 * @param practitioners - Array of organization practitioners
 * @returns The MRP practitioner's name, or empty string if not found
 */
export const resolveMrpName = (
  careTeam: fhirR4.CareTeam | undefined,
  practitioners: OrganizationPractitioner[]
): string => {
  const mrpRef = getMrpFromCareTeam(careTeam);
  if (!mrpRef) {
    return "";
  }
  return practitioners.find(p => p.reference.reference === mrpRef)?.name || "";
};

/**
 * Collect unique Practitioner IDs referenced by a CareTeam's participants.
 * Resolves both direct `Practitioner/` refs and `PractitionerRole/` refs
 * (via the provided roles) into bare Practitioner IDs.
 */
export const getCareTeamPractitionerIds = (
  careTeam: fhirR4.CareTeam | undefined,
  roles: fhirR4.PractitionerRole[] = []
): Set<string> => {
  const ids = new Set<string>();
  if (!careTeam?.participant) {
    return ids;
  }

  const rolesById = new Map<string, fhirR4.PractitionerRole>();
  for (const r of roles) {
    if (r?.id) {
      rolesById.set(r.id, r);
    }
  }

  const extractId = (ref?: string): string => {
    if (!ref || !ref.includes("/")) {
      return "";
    }
    return ref.split("/")[1] ?? "";
  };

  for (const participant of careTeam.participant) {
    if (isPractitionerParticipant(participant)) {
      const pid = getPractitionerId(participant);
      if (pid) {
        ids.add(pid);
      }
    } else if (isPractitionerRoleParticipant(participant)) {
      const roleId = extractId(participant.member?.reference);
      const role = rolesById.get(roleId);
      const pid = extractId(role?.practitioner?.reference);
      if (pid) {
        ids.add(pid);
      }
    }
  }

  return ids;
};
